import { SolicitudTipo } from '../../shared/interfaces/chat.interface';

/** Valor que usa el backend para la persona sin celula (muelle). */
export const SIN_LIDER = 'sin lider';

/** 'eliminar' y 'muelle' solo existen para la solicitud de eliminacion. */
export type SolicitudAccion = 'aceptar' | 'rechazar' | 'eliminar' | 'muelle';

export interface SolicitudBannerAccion {
  tipo: SolicitudTipo;
  accion: SolicitudAccion;
}

/** Qué hacer con los discípulos de un líder que se envía al muelle. */
export type DiscipulosAlMuelle = 'transferir' | 'sin-lider';

export interface ResumenDiscipulos {
  lider: boolean;
  total: number;
}

export interface ResolverSolicitudInput extends SolicitudBannerAccion {
  personaId: string;
  /** Obligatorio en los rechazos y al enviar al muelle. */
  motivo?: string;
  /** Solo al enviar un líder al muelle. */
  discipulos?: DiscipulosAlMuelle;
  /** Líder que recibe a los discípulos cuando discipulos es transferir. */
  idLiderDestino?: string;
}

export const SOLICITUD_BANNER: Record<SolicitudTipo, { etiqueta: string; icono: string }> = {
  eliminar: { etiqueta: 'Motivo de eliminaci\u00f3n', icono: 'pi-trash' },
  nivel: { etiqueta: 'Solicitud de nivel', icono: 'pi-arrow-up' },
  bautismo: { etiqueta: 'Solicitud de bautismo', icono: 'pi-heart' },
  bajaBautismo: { etiqueta: 'Solicitud de baja de bautismo', icono: 'pi-ban' },
  estudio: { etiqueta: 'Solicitud de estudio', icono: 'pi-book' },
};

const NOTA_ACEPTADA: Record<SolicitudTipo, string> = {
  eliminar: 'Eliminaci\u00f3n aceptada.',
  nivel: 'Nivel aceptado.',
  bautismo: 'Bautismo aceptado.',
  bajaBautismo: 'Baja de bautismo aceptada.',
  estudio: 'Estudio aceptado.',
};

const NOTA_RECHAZADA: Record<SolicitudTipo, string> = {
  eliminar: 'Eliminaci\u00f3n rechazada',
  nivel: 'Nivel rechazado',
  bautismo: 'Bautismo rechazado',
  bajaBautismo: 'Baja de bautismo rechazada',
  estudio: 'Estudio rechazado',
};

/** Nota de sistema que queda en el chat al resolver una solicitud. */
export function notaSistemaSolicitud(input: ResolverSolicitudInput): string {
  const motivo = input.motivo?.trim() ?? '';

  if (input.accion === 'muelle') {
    return `En el muelle: ${motivo}`;
  }
  if (input.accion === 'rechazar') {
    return `${NOTA_RECHAZADA[input.tipo]}: ${motivo}`;
  }
  return NOTA_ACEPTADA[input.tipo];
}

export interface SolicitudAccionCopy {
  /** Pide el motivo en un dialogo en lugar de confirmar directo. */
  pideMotivo: boolean;
  header: string;
  /** Texto del confirm; vacio cuando se pide motivo. */
  mensaje: string;
  aceptar: string;
  icono: string;
  destructiva: boolean;
  exito: string;
}

interface SolicitudAccionContexto {
  tipo: SolicitudTipo;
  accion: SolicitudAccion;
  nombre: string;
  nivelPersonal: number;
}

const CONFIRMACIONES: Record<string, (contexto: SolicitudAccionContexto) => SolicitudAccionCopy> = {
  'eliminar:eliminar': ({ nombre }) => ({
    pideMotivo: false,
    header: 'Seguro?',
    mensaje: `Si confirma, ${nombre} ser\u00e1 enviado/a a la papelera de reciclaje de datos.`,
    aceptar: 'Si, eliminar!',
    icono: 'pi pi-trash',
    destructiva: true,
    exito: 'Persona enviada a la papelera.',
  }),
  'eliminar:muelle': () => ({
    pideMotivo: true,
    header: 'Enviar al muelle',
    mensaje: '',
    aceptar: 'Enviar al muelle',
    icono: 'pi pi-inbox',
    destructiva: false,
    exito: 'Persona enviada al muelle.',
  }),
  'eliminar:rechazar': () => ({
    pideMotivo: true,
    header: 'Rechazar eliminaci\u00f3n',
    mensaje: '',
    aceptar: 'Enviar',
    icono: 'pi pi-times',
    destructiva: false,
    exito: 'Eliminaci\u00f3n rechazada.',
  }),
  'nivel:aceptar': ({ nombre, nivelPersonal }) => ({
    pideMotivo: false,
    header: 'Subir nivel',
    mensaje: `Si acepta, ${nombre} pasa a nivel ${nivelPersonal + 1}.`,
    aceptar: 'Aceptar nivel',
    icono: 'pi pi-arrow-up',
    destructiva: false,
    exito: 'Nivel aceptado.',
  }),
  'nivel:rechazar': () => ({
    pideMotivo: true,
    header: 'Rechazar nivel',
    mensaje: '',
    aceptar: 'Enviar',
    icono: 'pi pi-times',
    destructiva: false,
    exito: 'Nivel rechazado.',
  }),
  'bautismo:aceptar': ({ nombre }) => ({
    pideMotivo: false,
    header: 'Aceptar bautismo',
    mensaje: `Si acepta, ${nombre} se agrega a la secci\u00f3n de Est. / Bautismo.`,
    aceptar: 'Aceptar bautismo',
    icono: 'pi pi-heart',
    destructiva: false,
    exito: 'Bautismo aceptado.',
  }),
  'bautismo:rechazar': () => ({
    pideMotivo: true,
    header: 'Rechazar bautismo',
    mensaje: '',
    aceptar: 'Enviar',
    icono: 'pi pi-times',
    destructiva: false,
    exito: 'Bautismo rechazado.',
  }),
  'bajaBautismo:aceptar': ({ nombre }) => ({
    pideMotivo: false,
    header: 'Baja de bautismo',
    mensaje: `Si acepta, se revierte la inscripci\u00f3n o el bautismo de ${nombre}.`,
    aceptar: 'Aceptar baja',
    icono: 'pi pi-ban',
    destructiva: false,
    exito: 'Baja de bautismo aceptada.',
  }),
  'bajaBautismo:rechazar': () => ({
    pideMotivo: true,
    header: 'Rechazar baja de bautismo',
    mensaje: '',
    aceptar: 'Enviar',
    icono: 'pi pi-times',
    destructiva: false,
    exito: 'Baja de bautismo rechazada.',
  }),
  'estudio:aceptar': ({ nombre }) => ({
    pideMotivo: false,
    header: 'Aceptar estudio',
    mensaje: `Si acepta, ${nombre} se agrega a la secci\u00f3n de Estudiantes.`,
    aceptar: 'Aceptar estudio',
    icono: 'pi pi-book',
    destructiva: false,
    exito: 'Estudio aceptado.',
  }),
  'estudio:rechazar': () => ({
    pideMotivo: true,
    header: 'Rechazar estudio',
    mensaje: '',
    aceptar: 'Enviar',
    icono: 'pi pi-times',
    destructiva: false,
    exito: 'Estudio rechazado.',
  }),
};

export function solicitudAccionCopy(contexto: SolicitudAccionContexto): SolicitudAccionCopy | null {
  const build = CONFIRMACIONES[`${contexto.tipo}:${contexto.accion}`];
  return build ? build(contexto) : null;
}
